import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EditorProvider } from '@tiptap/react';
import { debounce } from 'lodash';

import useOutlineAutoVersioning from './hooks/outline/useOutlineAutoVersioning';
import useUpdateOutlineEditorContent from './hooks/outline/useUpdateOutlineEditorContent';

import { editorAutoSaveDelay, editorTypes } from './constants/tiptapEditorConstants';
import { outlineEditorExtensions } from './extensions/outlineEditorExtensions';

import DraggableComponent from './components/DraggableComponent';
import EditLinkContent from './toolbar/buttons/content/EditLinkContent';
import OutlineEditorToolbar from './toolbar/outline/OutlineEditorToolbar';

import { selectAIContentLoading } from '../../pages/contentOptimizer/contentPage/content/briefTab/content/tabsContent/store/contentBriefsOutline.selectors';
import { clearEditorByType } from './store/tiptapEditor.actions';
import { selectCurrentEditorContent } from './store/tiptapEditor.selectors';

import './TipTapEditor.scss';

const editorType = editorTypes.OUTLINE;

const OutlineEditor = () => {
    const dispatch = useDispatch();

    const editorRef = useRef(null);
    const lastContentRef = useRef('');

    const currentEditorContent = useSelector(selectCurrentEditorContent(editorType));
    const aiContentLoading = useSelector(selectAIContentLoading);

    const handleUpdateEditor = useUpdateOutlineEditorContent();

    useOutlineAutoVersioning();

    const handleEditorReady = useCallback(
        (editorData) => {
            const { editor } = editorData;

            if (editor) {
                editorRef.current = editor;

                if (editor.getHTML() !== currentEditorContent) {
                    editor.commands.setContent(currentEditorContent);
                }
            }
        },
        [currentEditorContent]
    );

    const debouncedEditorChange = useCallback(
        debounce((updatedContent) => {
            if (updatedContent !== lastContentRef.current) {
                lastContentRef.current = updatedContent;
                handleUpdateEditor(updatedContent);
            }
        }, editorAutoSaveDelay),
        [handleUpdateEditor]
    );

    const handleEditorChange = useCallback(
        (editorOnChangeData) => {
            const { editor } = editorOnChangeData;
            const updatedContent = editor?.getHTML();
            debouncedEditorChange(updatedContent);
        },
        [debouncedEditorChange]
    );

    const handleEditorDestroy = useCallback(() => {
        dispatch(clearEditorByType(editorType));
    }, [dispatch]);

    useEffect(() => {
        if (editorRef.current) {
            editorRef.current.setEditable(!aiContentLoading);
        }
    }, [aiContentLoading]);

    useEffect(() => {
        return () => {
            debouncedEditorChange.cancel();
        };
    }, [debouncedEditorChange]);

    return (
        <EditorProvider
            slotBefore={
                <>
                    <OutlineEditorToolbar />
                    <EditLinkContent />
                </>
            }
            extensions={outlineEditorExtensions}
            onCreate={handleEditorReady}
            onUpdate={handleEditorChange}
            onDestroy={handleEditorDestroy}
            content={currentEditorContent}
        >
            <DraggableComponent fixedH1 />
        </EditorProvider>
    );
};

export default React.memo(OutlineEditor);
